import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BadgeCheck, ShieldAlert, ShieldX, Loader2, ScanLine } from "lucide-react";
import { api, BACKEND_URL } from "@/lib/api";

const TONE = {
  ACTIVE: ["bg-emerald-500 text-white", BadgeCheck, "Verified and active with Karmivo"],
  SUSPENDED: ["bg-amber-500 text-slate-950", ShieldAlert, "Suspended — do not engage for Karmivo work"],
  REVOKED: ["bg-red-600 text-white", ShieldX, "ID revoked — no longer valid"],
};

// Accepts "KRV-2026-0001", "krv 2026 0001" or "KRV20260001".
export const normalizeWorkerId = (raw) => {
  const s = raw.toUpperCase().replace(/[^A-Z0-9]/g, "");
  const m = s.match(/^KRV(\d{4})(\d{4,})$/);
  return m ? `KRV-${m[1]}-${m[2]}` : null;
};

export function VerifyWidget() {
  const [q, setQ] = useState("");
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const check = async (e) => {
    e.preventDefault();
    const code = normalizeWorkerId(q);
    setD(null); setErr("");
    if (!code) { setErr("Enter a Worker ID like KRV-2026-0001"); return; }
    setBusy(true);
    try { setD((await api.get(`/verify/${code}`)).data); }
    catch (x) { setErr(x.response?.status === 404 ? `No Karmivo worker found with ID ${code}. The card may be fake.` : "Could not check right now. Please try again."); }
    finally { setBusy(false); }
  };
  const [cls, Icon, note] = d ? TONE[d.status] || TONE.REVOKED : [];

  return (
    <section className="max-w-7xl mx-auto px-6 py-16" data-testid="verify-widget">
      <div className="grid lg:grid-cols-[1fr_1.1fr] gap-8 items-center rounded-3xl bg-zinc-950 text-white p-6 sm:p-10 relative overflow-hidden">
        <div className="absolute right-0 top-0 w-48 h-48 rounded-full bg-orange-500/20 blur-3xl pointer-events-none"/>
        <div className="relative">
          <div className="text-[10px] uppercase tracking-[0.25em] text-orange-400 font-bold flex items-center gap-2"><ScanLine size={14}/>Verify a worker</div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold mt-2">Check a Karmivo Worker ID</h2>
          <p className="text-zinc-400 mt-3 max-w-md">Every Karmivo worker carries a photo ID card. Enter the Worker ID printed on it to see their live verification status.</p>
          <form onSubmit={check} className="flex gap-2 mt-6 max-w-md">
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="KRV-2026-0001" className="h-12 bg-zinc-900 border-zinc-700 text-white font-mono uppercase" data-testid="verify-widget-input"/>
            <Button className="h-12 px-6 bg-orange-500 hover:bg-orange-600" disabled={busy} data-testid="verify-widget-btn">{busy ? <Loader2 size={18} className="animate-spin"/> : "Check"}</Button>
          </form>
        </div>
        <div className="relative min-h-[180px]" aria-live="polite">
          {!d && !err && <div className="h-full rounded-2xl border border-dashed border-zinc-800 flex items-center justify-center text-sm text-zinc-500 p-8 text-center" data-testid="verify-widget-idle">The result appears here. You can also scan the QR code on the card.</div>}
          {err && <div className="rounded-2xl border border-red-500/40 bg-red-500/10 p-6 text-sm text-red-200" data-testid="verify-widget-error"><ShieldX className="text-red-400 mb-2" size={24}/>{err}</div>}
          {d && (
            <div className="rounded-2xl border border-zinc-800 bg-zinc-900 overflow-hidden" data-testid="verify-widget-result">
              <div className={`px-5 py-2.5 flex items-center gap-2 font-bold tracking-wider text-sm ${cls}`} data-testid="verify-widget-status"><Icon size={18}/>{d.status}</div>
              <div className="p-5 flex gap-4">
                {d.photo_available
                  ? <img src={`${BACKEND_URL}/api/verify/${d.worker_code}/photo`} alt={d.name} className="w-20 h-28 object-cover rounded-lg border-2 border-orange-500"/>
                  : <div className="w-20 h-28 rounded-lg bg-zinc-800 shrink-0"/>}
                <div className="min-w-0">
                  <div className="font-display text-xl font-bold" data-testid="verify-widget-name">{d.name}</div>
                  <div className="font-mono text-orange-400 text-sm">{d.worker_code}</div>
                  <div className="text-xs text-zinc-400 mt-2">{d.categories.join(" · ")}</div>
                  <div className="text-xs text-zinc-300 mt-3">{note}</div>
                  <Link to={`/verify/${d.worker_code}`} className="inline-block text-xs text-orange-400 mt-3 font-semibold" data-testid="verify-widget-details">Full verification page →</Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
